import type { UserExtraDetailsPayload } from './parseUserExtraDetails';

export type UserFormExtraPermissionState = {
  project: Set<string>;
  analysis: Set<string>;
  pipeInventory: Set<string>;
  map: Set<string>;
  devices: Set<string>;
  showProjectAdvanced: boolean;
  showAnalysisAdvanced: boolean;
  showPipeInventoryAdvanced: boolean;
  showMapAdvanced: boolean;
  showDevicesAdvanced: boolean;
};

const emptySets = (): UserFormExtraPermissionState => ({
  project: new Set(),
  analysis: new Set(),
  pipeInventory: new Set(),
  map: new Set(),
  devices: new Set(),
  showProjectAdvanced: false,
  showAnalysisAdvanced: false,
  showPipeInventoryAdvanced: false,
  showMapAdvanced: false,
  showDevicesAdvanced: false,
});

/** Map saved user_extra_details into UserForm checkbox state. */
export function userExtraDetailsToFormState(
  extra: UserExtraDetailsPayload | null | undefined
): UserFormExtraPermissionState {
  if (!extra) return emptySets();

  const state = emptySets();

  if (Array.isArray(extra.project) && extra.project.length > 0) {
    state.project = new Set(extra.project);
    state.showProjectAdvanced = true;
  }

  if (Array.isArray(extra.analysis) && extra.analysis.length > 0) {
    const analysisOnly = extra.analysis.filter((id) => id !== 'view_pipe_inventory');
    state.analysis = new Set(analysisOnly);
    state.showAnalysisAdvanced = analysisOnly.length > 0;
  }

  const pipeFromSection = Array.isArray(extra.pipe_inventory) ? extra.pipe_inventory : [];
  const pipeFromLegacyAnalysis =
    Array.isArray(extra.analysis) && extra.analysis.includes('view_pipe_inventory')
      ? ['view_pipe_inventory']
      : [];
  const pipeMerged = Array.from(
    new Set(
      [...pipeFromSection, ...pipeFromLegacyAnalysis].map((id) =>
        id === 'add_pipe_inventory' ? 'edit_pipe_inventory' : id
      )
    )
  );
  if (pipeMerged.length > 0) {
    state.pipeInventory = new Set(pipeMerged);
    state.showPipeInventoryAdvanced = true;
  }

  if (Array.isArray(extra.map) && extra.map.length > 0) {
    state.map = new Set(extra.map);
    state.showMapAdvanced = true;
  }

  if (Array.isArray(extra.devices) && extra.devices.length > 0) {
    state.devices = new Set(extra.devices);
    state.showDevicesAdvanced = true;
  }

  return state;
}

export type UserExtraDetailsSelectionInput = {
  project: Set<string>;
  analysis: Set<string>;
  pipeInventory: Set<string>;
  map: Set<string>;
  devices: Set<string>;
};

/** Build API payload for create/update user (only non-empty sections). */
export function buildUserExtraDetailsPayload(
  selection: UserExtraDetailsSelectionInput
): UserExtraDetailsPayload {
  const extra: UserExtraDetailsPayload = {};

  if (selection.project.size > 0) {
    extra.project = Array.from(selection.project);
  }
  if (selection.analysis.size > 0) {
    extra.analysis = Array.from(selection.analysis);
  }
  if (selection.pipeInventory.size > 0) {
    extra.pipe_inventory = Array.from(selection.pipeInventory);
  }
  if (selection.map.size > 0) {
    extra.map = Array.from(selection.map);
  }
  if (selection.devices.size > 0) {
    extra.devices = Array.from(selection.devices);
  }

  return extra;
}
